import { create } from 'zustand';
import { io } from 'socket.io-client';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api';
const SOCKET_URL = process.env.REACT_APP_URL?.replace(/\/$/, '') || 'http://127.0.0.1:8000';

let socket = null;

export const useChatStore = create((set, get) => ({
    // State
    conversations: [],
    activeConversation: null,
    messages: [],
    unreadCount: 0,
    loading: false,
    messagesLoading: false,
    connected: false,
    typingUsers: {},

    // Socket ulanish
    connect: (token) => {
        if (socket?.connected) return;

        socket = io(SOCKET_URL, {
            auth: { token },
            transports: ['websocket', 'polling'],
        });

        socket.on('connect', () => {
            console.log('Chat connected');
            set({ connected: true });
            get().fetchUnreadCount(token);
        });

        socket.on('disconnect', () => {
            set({ connected: false });
        });

        // Yangi xabar kelganda
        socket.on('message:new', (message) => {
            const { activeConversation, messages } = get();
            if (activeConversation && message.conversation_id === activeConversation.id) {
                // Dublikatni oldini olish
                const exists = messages.some(m => m.id === message.id);
                if (!exists) {
                    set({ messages: [...messages, message] });
                }
            }
            get().updateConversationLastMessage(message);
        });

        // O'qildi tasdiqi
        socket.on('message:read', ({ conversationId }) => {
            const { activeConversation, messages } = get();
            if (activeConversation && conversationId === activeConversation.id) {
                set({
                    messages: messages.map(m => ({ ...m, is_read: 1 }))
                });
            }
        });

        // Typing
        socket.on('typing:start', ({ conversationId, userId }) => {
            set(state => ({
                typingUsers: { ...state.typingUsers, [conversationId]: userId }
            }));
        });

        socket.on('typing:stop', ({ conversationId }) => {
            set(state => {
                const typing = { ...state.typingUsers };
                delete typing[conversationId];
                return { typingUsers: typing };
            });
        });
    },

    // Socket uzish
    disconnect: () => {
        if (socket) {
            socket.disconnect();
            socket = null;
        }
        set({ connected: false, conversations: [], messages: [], activeConversation: null });
    },

    // Suhbatlar ro'yxatini olish (REST)
    fetchConversations: async (token) => {
        set({ loading: true });
        try {
            const res = await axios.get(`${API_URL}/chat/conversations`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const list = res.data?.data || [];
            // Normalize: frontend kutgan fieldlarni qo'shish
            const normalized = list.map(conv => ({
                ...conv,
                other_user_id: conv.partner?.id,
                other_user_name: conv.partner?.fullname || 'Foydalanuvchi',
                other_user_avatar: conv.partner?.avatar || null,
                provider_name: conv.service?.name || '',
                last_message: conv.last_message?.text || '',
            }));
            set({ conversations: normalized, loading: false });
        } catch (err) {
            console.error('Fetch conversations error:', err);
            set({ loading: false });
        }
    },

    // Suhbatni ochish va xabarlarni yuklash
    openConversation: async (conversation, token) => {
        set({ activeConversation: conversation, messagesLoading: true, messages: [] });

        // Socket orqali room ga qo'shilish + o'qildi belgilash
        if (socket?.connected) {
            socket.emit('conversation:open', {
                partnerId: conversation.other_user_id,
                servicesId: conversation.services_id || null,
            }, () => {});
            socket.emit('message:read', { conversationId: conversation.id });
        }

        try {
            const res = await axios.get(
                `${API_URL}/chat/conversations/${conversation.id}/messages`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            set({ messages: res.data?.data?.messages || [], messagesLoading: false });

            // Unread count yangilash
            set(state => ({
                conversations: state.conversations.map(c =>
                    c.id === conversation.id ? { ...c, unread_count: 0 } : c
                ),
                unreadCount: Math.max(0, state.unreadCount - (conversation.unread_count || 0))
            }));
        } catch (err) {
            console.error('Fetch messages error:', err);
            set({ messagesLoading: false });
        }
    },

    // Suhbatdan chiqish
    closeConversation: () => {
        set({ activeConversation: null, messages: [] });
    },

    // Xabar yuborish (socket callback orqali)
    sendMessage: (text) => {
        const { activeConversation } = get();
        if (!activeConversation || !text.trim() || !socket?.connected) return;

        socket.emit('message:send', {
            conversationId: activeConversation.id,
            text: text.trim(),
        });
    },

    // Yangi suhbat yaratish (socket orqali)
    createConversation: (partnerId, servicesId, token) => {
        return new Promise((resolve) => {
            if (!socket?.connected) {
                get().connect(token);
                // Socket ulanishini kutish
                const waitConnect = setInterval(() => {
                    if (socket?.connected) {
                        clearInterval(waitConnect);
                        doOpen();
                    }
                }, 200);
                setTimeout(() => clearInterval(waitConnect), 5000);
            } else {
                doOpen();
            }

            function doOpen() {
                socket.emit('conversation:open', {
                    partnerId,
                    servicesId: servicesId || null,
                }, async (res) => {
                    if (res.ok) {
                        await get().fetchConversations(token);
                        const conv = get().conversations.find(c => c.id === res.conversation.id);
                        resolve(conv || res.conversation);
                    } else {
                        resolve(null);
                    }
                });
            }
        });
    },

    // Typing
    startTyping: () => {
        const { activeConversation } = get();
        if (activeConversation && socket?.connected) {
            socket.emit('typing:start', { conversationId: activeConversation.id });
        }
    },

    stopTyping: () => {
        const { activeConversation } = get();
        if (activeConversation && socket?.connected) {
            socket.emit('typing:stop', { conversationId: activeConversation.id });
        }
    },

    // O'qilmagan xabarlar soni (REST)
    fetchUnreadCount: async (token) => {
        try {
            const res = await axios.get(`${API_URL}/chat/unread`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            set({ unreadCount: res.data?.data?.unread || 0 });
        } catch (err) {
            console.error('Unread count error:', err);
        }
    },

    // Conversation last message yangilash (ichki)
    updateConversationLastMessage: (message) => {
        set(state => ({
            conversations: state.conversations.map(c =>
                c.id === message.conversation_id
                    ? {
                        ...c,
                        last_message: message.text,
                        last_message_at: message.created_at,
                        unread_count: state.activeConversation?.id === message.conversation_id
                            ? c.unread_count
                            : (c.unread_count || 0) + 1
                    }
                    : c
            ).sort((a, b) => new Date(b.last_message_at) - new Date(a.last_message_at))
        }));
    },
}));
